import React, { useEffect, useState, useMemo, useRef } from "react";
import { Modal, Button, CircularProgress, LinearProgress, Typography, Box, Slider, FormControl, Select, MenuItem } from "@mui/material";
import ReactPlayer from "react-player";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import config from "../../js/config";
import { getBearerString } from "../../js/jwt_token";

export default function ReviewPIIModal({ open, onClose, cameras, bucket_name_file_path }) {
    const [videoUrls, setVideoUrls] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [loadingProgress, setLoadingProgress] = useState(0);

    // Unified playback state
    const [isPlaying, setIsPlaying] = useState(true);
    const [playedFraction, setPlayedFraction] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isSeeking, setIsSeeking] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const playerRefs = useRef<{ [key: string]: ReactPlayer | null }>({});

    const playbackRateOptions = [0.5, 0.75, 1, 1.25, 1.5, 2];

    useEffect(() => {
        if (open && cameras.length > 0) {
            loadAllVideos();
        }
        
        // Cleanup function to revoke object URLs and reset states when modal closes
        return () => {
            // Revoke all object URLs to prevent memory leaks
            Object.values(videoUrls).forEach(url => {
                if (url) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [open, cameras, bucket_name_file_path]);

    // Reset states when modal closes (separate effect to avoid dependency issues)
    useEffect(() => {
        if (!open) {
            // Revoke URLs before clearing them
            Object.values(videoUrls).forEach(url => {
                if (url) {
                    URL.revokeObjectURL(url);
                }
            });
            setLoading(true);
            setLoadingProgress(0);
            setError(null);
            setVideoUrls({});
        }
    }, [open]);

    const loadAllVideos = async () => {
        setLoading(true);
        setError(null);
        setLoadingProgress(0);

        // Start progress timer (90 seconds)
        const progressTimer = setInterval(() => {
            setLoadingProgress(prev => {
                const newProgress = prev + (100 / 90); // Increment by 1/90th every second
                return newProgress >= 100 ? 100 : newProgress;
            });
        }, 1000);

        try {
            const videoPromises = cameras.map(async (camera) => {
                const link = `${config.data_platform_api_url}/lfds/get_video?bucket_name_file_path=${bucket_name_file_path}&camera_name=${camera}`;
                return fetchVideo(link, camera);
            });

            const videos = await Promise.all(videoPromises);
            setVideoUrls(Object.fromEntries(videos));
        } catch (error) {
            console.error("Error loading videos:", error);
            setError("Failed to load videos. Please try again later.");
        } finally {
            clearInterval(progressTimer);
            setLoadingProgress(100);
            setLoading(false);
        }
    };

    const fetchVideo = async (link: string, camera: string) => {
        const response = await fetch(link, {
            headers: { Authorization: getBearerString() },
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const body = response.body;
        if (!body) throw new Error("No response body.");

        const reader = body.getReader();
        const chunks: Uint8Array[] = [];
        await readChunks(reader, chunks);

        const blob = new Blob(chunks as BlobPart[], { type: "video/mp4" });
        const url = URL.createObjectURL(blob);

        return [camera, url];
    };

    const readChunks = async (reader: ReadableStreamDefaultReader, chunks: Uint8Array[]): Promise<void> => {
        const { done, value } = await reader.read();
        if (done) return;
        chunks.push(value);
        await readChunks(reader, chunks);
    };

    const handleSetApproval = async () => handleSetPIIApproval(true);
    const handleSetUnapproval = async () => handleSetPIIApproval(false);

    const handleSetPIIApproval = async (approved: boolean) => {
        try {
            const response = await fetch(`${config.data_platform_api_url}/lfds/pii/set_approval`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: getBearerString(),
                },
                body: JSON.stringify({
                    bucket_name_file_path,
                    pii_approved: approved,
                }),
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            alert(`PII ${approved ? "approved" : "unapproved"} successfully.`);
        } catch (error) {
            console.error("Error setting PII approval:", error);
            alert("Failed to set PII approval. Please try again later.");
        }
    };

    const handleSetReviewed = async () => {
        try {
            const response = await fetch(`${config.data_platform_api_url}/lfds/pii/set_reviewed`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: getBearerString(),
                },
                body: JSON.stringify({
                    bucket_name_file_path,
                    pii_reviewed: true,
                }),
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            alert("PII reviewed set successfully.");
        } catch (error) {
            console.error("Error setting PII reviewed:", error);
            alert("Failed to set PII reviewed. Please try again later.");
        }
    };

    // --- Unified Playback Controls ---
    const handlePlayPause = () => {
        setIsPlaying(prev => !prev);
    };

    const handleProgress = (state: { played: number }) => {
        if (!isSeeking) {
            setPlayedFraction(state.played);
        }
    };

    const handleDuration = (dur: number) => {
        if (dur > duration) {
            setDuration(dur);
        }
    };

    const handleSeekChange = (_event: Event, newValue: number | number[]) => {
        setIsSeeking(true);
        setPlayedFraction(newValue as number);
    };

    const handleSeekCommit = (_event: React.SyntheticEvent | Event, newValue: number | number[]) => {
        setIsSeeking(false);
        const fraction = newValue as number;
        Object.values(playerRefs.current).forEach(player => {
            if (player) {
                player.seekTo(fraction, 'fraction');
            }
        });
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // --- Centralized Styles ---
    // Grouping styles here makes the JSX cleaner and future style changes easier.
    const styles: { [key: string]: React.CSSProperties } = {
        scrollableContainer: {
            flex: 1, // Allows this div to grow and fill available vertical space.
            overflowY: 'auto', // Adds a scrollbar ONLY if the content is too tall.
            width: '100%',
            boxSizing: 'border-box',
            paddingRight: '10px',
            marginBottom: '20px'
        },
        videoRow: {
            display: 'flex',
            gap: '15px',
            marginBottom: '25px'
        },
        videoColumn: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
        },
        playerWrapper: {
            position: 'relative' // Serves as the positioning anchor for the label overlay.
        },
        labelOverlay: {
            position: 'absolute',
            top: '8px',
            left: '8px',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontWeight: 'bold',
            zIndex: 1
        }
    };

    // --- Performance Optimization ---
    // `useMemo` performs camera classification and prevents this logic from re-running
    // on every render. It only recalculates if the `cameras` prop itself changes.
    const cameraGroups = useMemo(() => {
        // Classify cameras into a 2x3 grid layout: eye vs. other & left/right/middle.
        const leftEyeCameras = cameras.filter(cam => cam.toLowerCase().includes('eye') && cam.toLowerCase().includes('left'));
        const rightEyeCameras = cameras.filter(cam => cam.toLowerCase().includes('eye') && cam.toLowerCase().includes('right'));
        const leftOtherCameras = cameras.filter(cam => !cam.toLowerCase().includes('eye') && cam.toLowerCase().includes('left'));
        const rightOtherCameras = cameras.filter(cam => !cam.toLowerCase().includes('eye') && cam.toLowerCase().includes('right'));
        const classifiedCameras = [...leftEyeCameras, ...rightEyeCameras, ...leftOtherCameras, ...rightOtherCameras];
        const middleOtherCameras = cameras.filter(cam => !classifiedCameras.includes(cam));
        return { leftEyeCameras, rightEyeCameras, leftOtherCameras, rightOtherCameras, middleOtherCameras };
    }, [cameras]);

    // --- Helper Function ---
    // Formats a 'snake_case_id' into a readable 'Title Case Name'.
    const formatName = (rawName) => {
        if (!rawName) return '';
        return rawName
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    const renderVideoPlayer = (camera: string) => (
        <div key={camera} style={styles.playerWrapper}>
            <ReactPlayer
                ref={(ref) => { playerRefs.current[camera] = ref; }}
                url={videoUrls[camera]}
                playing={isPlaying}
                playbackRate={playbackRate}
                onProgress={handleProgress}
                onDuration={handleDuration}
                width="100%"
                height="auto"
                muted
            />
            <div style={styles.labelOverlay}>{formatName(camera)}</div>
        </div>
    );

    return (
        <Modal open={open} onClose={onClose} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {/* The main content area uses a column flex layout to pin the action buttons to the bottom. */}
            <div style={{
                display: "flex",
                flexDirection: "column",
                width: "70%",
                height: "95%",
                maxHeight: "95vh",
                background: "white",
                padding: "20px",
                borderRadius: "8px"
            }}>
                {/* Main content area - shows loading or videos */}
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {loading ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                            gap={3}
                        >
                            <CircularProgress size={60} thickness={4} />
                            <Typography variant="h6" color="textSecondary">
                                Loading
                            </Typography>
                            <Box width="300px" display="flex" flexDirection="column" gap={1}>
                                <LinearProgress
                                    variant="determinate"
                                    value={loadingProgress}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: '#e0e0e0',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 4,
                                        }
                                    }}
                                />
                                <Typography variant="body2" color="textSecondary" textAlign="center">
                                    {Math.round(loadingProgress)}%
                                </Typography>
                            </Box>
                        </Box>
                    ) : error ? (
                        <p>{error}</p>
                    ) : (
                        <div style={styles.scrollableContainer}>
                            {/* TOP TIER: Renders cameras with "eye" in the name. */}
                            <div style={styles.videoRow}>
                                <div style={styles.videoColumn}>
                                    {cameraGroups.leftEyeCameras.map(renderVideoPlayer)}
                                </div>
                                <div style={styles.videoColumn}>
                                    {cameraGroups.rightEyeCameras.map(renderVideoPlayer)}
                                </div>
                            </div>

                            {/* BOTTOM TIER: Renders all other cameras. */}
                            <div style={{ ...styles.videoRow, marginBottom: 0 }}>
                                <div style={styles.videoColumn}>
                                    {cameraGroups.leftOtherCameras.map(renderVideoPlayer)}
                                </div>
                                {cameraGroups.middleOtherCameras.length > 0 && (
                                    <div style={styles.videoColumn}>
                                        {cameraGroups.middleOtherCameras.map(renderVideoPlayer)}
                                    </div>
                                )}
                                <div style={styles.videoColumn}>
                                    {cameraGroups.rightOtherCameras.map(renderVideoPlayer)}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Unified playback scrubber - always visible when not in error state */}
                {!error && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        px: 2,
                        py: 1,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                        flexShrink: 0,
                        mb: 2
                    }}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handlePlayPause}
                            disabled={loading}
                            sx={{ minWidth: 40, p: 1 }}
                        >
                            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                        </Button>
                        <Typography variant="body2" sx={{ minWidth: 45 }}>
                            {formatTime(playedFraction * duration)}
                        </Typography>
                        <Slider
                            value={playedFraction}
                            min={0}
                            max={1}
                            step={0.001}
                            onChange={handleSeekChange}
                            onChangeCommitted={handleSeekCommit}
                            disabled={loading}
                            sx={{ flex: 1 }}
                        />
                        <Typography variant="body2" sx={{ minWidth: 45 }}>
                            {formatTime(duration)}
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                            <Select
                                value={playbackRate}
                                onChange={(e) => setPlaybackRate(e.target.value as number)}
                                disabled={loading}
                                sx={{ fontSize: '0.875rem' }}
                            >
                                {playbackRateOptions.map((rate) => (
                                    <MenuItem key={rate} value={rate}>
                                        {rate}x
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}

                <div style={{ display: "flex", justifyContent: "center", gap: "20px", flexShrink: 0 }}>
                    <Button variant="contained" color="primary" onClick={handleSetApproval}>
                        Approve
                    </Button>
                    <Button variant="contained" color="secondary" onClick={handleSetUnapproval}>
                        Disapprove
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleSetReviewed}>
                        Reviewed
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
